import type { SimulationModification, SimulationResults, SimulationScenario } from '../../types';

/**
 * Compare two simulation scenarios and return the differences
 */
export interface ScenarioComparison {
  scenario1: SimulationScenario;
  scenario2: SimulationScenario;
  differences: {
    conflictDelta: number;
    teacherLoadDelta: number;
    roomUtilizationDelta: number;
    curriculumCoverageDelta: number;
  };
  winner: 'scenario1' | 'scenario2' | 'tie';
  summary: string;
}

export function compareScenarios(
  scenario1: SimulationScenario,
  scenario2: SimulationScenario,
): ScenarioComparison | null {
  if (!scenario1.results || !scenario2.results) {
    return null;
  }

  const r1 = scenario1.results;
  const r2 = scenario2.results;

  const differences = {
    conflictDelta: r1.stats.conflictCount - r2.stats.conflictCount,
    teacherLoadDelta: r1.comparison.teacherLoadDelta - r2.comparison.teacherLoadDelta,
    roomUtilizationDelta: r1.comparison.roomUtilizationDelta - r2.comparison.roomUtilizationDelta,
    curriculumCoverageDelta: r1.stats.curriculumCoverage - r2.stats.curriculumCoverage,
  };

  // Calculate score based on key metrics (lower conflicts, higher coverage is better)
  const score1 = calculateScenarioScore(r1);
  const score2 = calculateScenarioScore(r2);

  const winner = score1 > score2 ? 'scenario1' : score2 > score1 ? 'scenario2' : 'tie';

  const summary = generateComparisonSummary(scenario1, scenario2, differences, winner);

  return {
    scenario1,
    scenario2,
    differences,
    winner,
    summary,
  };
}

/**
 * Calculate a score for a simulation result (higher is better)
 */
export function calculateScenarioScore(results: SimulationResults): number {
  if (!results.feasible) return -1000;

  let score = 0;

  // Base score from feasibility
  score += results.feasible ? 100 : 0;

  // Conflicts (fewer is better, max penalty -50)
  score -= results.stats.conflictCount * 5;

  // Rule violations
  score -= results.stats.ruleViolations.hard * 20;
  score -= results.stats.ruleViolations.soft * 2;

  // Curriculum coverage (0-100, target is 100)
  score += results.stats.curriculumCoverage * 0.5;

  // Teacher preference satisfaction
  score += results.stats.teacherPreferenceSatisfaction * 0.3;

  // Distribution score
  score += results.stats.distributionScore * 0.2;

  return Math.round(score * 100) / 100;
}

/**
 * Generate a human-readable summary of scenario comparison
 */
function generateComparisonSummary(
  scenario1: SimulationScenario,
  scenario2: SimulationScenario,
  differences: ScenarioComparison['differences'],
  winner: ScenarioComparison['winner'],
): string {
  const winnerName = winner === 'tie' ? 'Both scenarios' : winner === 'scenario1' ? scenario1.name : scenario2.name;

  const parts: string[] = [];

  if (winner === 'tie') {
    parts.push('Both scenarios produce similar results.');
  } else {
    parts.push(`${winnerName} performs better overall.`);
  }

  if (differences.conflictDelta !== 0) {
    const better = differences.conflictDelta > 0 ? scenario2.name : scenario1.name;
    parts.push(`${better} has ${Math.abs(differences.conflictDelta)} fewer conflicts.`);
  }

  if (Math.abs(differences.curriculumCoverageDelta) > 0.5) {
    const better = differences.curriculumCoverageDelta > 0 ? scenario1.name : scenario2.name;
    parts.push(`${better} has ${Math.abs(differences.curriculumCoverageDelta).toFixed(1)}% better curriculum coverage.`);
  }

  return parts.join(' ');
}

/**
 * Compare multiple scenarios and rank them
 */
export interface RankedScenario {
  scenario: SimulationScenario;
  score: number;
  rank: number;
  strengths: string[];
  weaknesses: string[];
}

export function rankScenarios(scenarios: SimulationScenario[]): RankedScenario[] {
  const completedScenarios = scenarios.filter((s) => s.status === 'completed' && s.results);

  const ranked = completedScenarios.map((scenario) => {
    const score = calculateScenarioScore(scenario.results!);
    const { strengths, weaknesses } = analyzeScenario(scenario);

    return {
      scenario,
      score,
      rank: 0, // Will be set after sorting
      strengths,
      weaknesses,
    };
  });

  // Sort by score descending
  ranked.sort((a, b) => b.score - a.score);

  // Assign ranks
  ranked.forEach((r, index) => {
    r.rank = index + 1;
  });

  return ranked;
}

/**
 * Analyze a scenario's strengths and weaknesses
 */
function analyzeScenario(scenario: SimulationScenario): { strengths: string[]; weaknesses: string[] } {
  const strengths: string[] = [];
  const weaknesses: string[] = [];

  const results = scenario.results;
  if (!results) return { strengths, weaknesses };

  // Feasibility
  if (results.feasible) {
    strengths.push('Feasible solution found');
  } else {
    weaknesses.push('No feasible solution');
  }

  // Conflicts
  if (results.stats.conflictCount === 0) {
    strengths.push('Zero conflicts');
  } else if (results.stats.conflictCount <= 3) {
    strengths.push('Low conflict count');
  } else if (results.stats.conflictCount > 10) {
    weaknesses.push('High number of conflicts');
  }

  // Rule violations
  if (results.stats.ruleViolations.hard === 0) {
    strengths.push('No hard rule violations');
  } else {
    weaknesses.push(`${results.stats.ruleViolations.hard} hard rule violation(s)`);
  }

  // Coverage
  if (results.stats.curriculumCoverage >= 99) {
    strengths.push('Excellent curriculum coverage');
  } else if (results.stats.curriculumCoverage >= 95) {
    strengths.push('Good curriculum coverage');
  } else if (results.stats.curriculumCoverage < 90) {
    weaknesses.push('Low curriculum coverage');
  }

  // Teacher satisfaction
  if (results.stats.teacherPreferenceSatisfaction >= 90) {
    strengths.push('High teacher satisfaction');
  } else if (results.stats.teacherPreferenceSatisfaction < 75) {
    weaknesses.push('Low teacher preference satisfaction');
  }

  // Comparison deltas
  if (results.comparison.conflictDelta < -3) {
    strengths.push(`Reduces conflicts by ${Math.abs(results.comparison.conflictDelta)}`);
  } else if (results.comparison.conflictDelta > 3) {
    weaknesses.push(`Increases conflicts by ${results.comparison.conflictDelta}`);
  }

  return { strengths, weaknesses };
}

/**
 * Validate if a modification is complete and valid
 */
export function validateModification(modification: SimulationModification): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Check type
  if (!['add', 'remove', 'modify'].includes(modification.type)) {
    errors.push('Invalid modification type');
  }

  // Check entity
  if (!['teacher', 'student', 'room', 'subject', 'rule'].includes(modification.entity)) {
    errors.push('Invalid entity type');
  }

  // For remove and modify, entityId is required
  if ((modification.type === 'remove' || modification.type === 'modify') && !modification.entityId) {
    errors.push('Entity ID is required for remove/modify operations');
  }

  // Check changes object
  if (!modification.changes || Object.keys(modification.changes).length === 0) {
    errors.push('Changes object cannot be empty');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Generate a description for a modification
 */
export function describeModification(modification: SimulationModification): string {
  const entityLabels: Record<string, string> = {
    teacher: 'Teacher',
    student: 'Student(s)',
    room: 'Room',
    subject: 'Subject',
    rule: 'Rule',
  };

  const entityLabel = entityLabels[modification.entity] || modification.entity;

  switch (modification.type) {
    case 'add':
      if (modification.entity === 'teacher') {
        const name = `${modification.changes.firstName || ''} ${modification.changes.lastName || ''}`.trim();
        return `Add new ${entityLabel}: ${name || 'Unknown'}`;
      }
      if (modification.entity === 'room') {
        return `Add new ${entityLabel}: ${modification.changes.name || 'Unknown'}`;
      }
      if (modification.entity === 'student') {
        return `Add ${modification.changes.count || 0} ${entityLabel} to ${modification.changes.yearGroup || 'unknown year'}`;
      }
      return `Add new ${entityLabel}`;

    case 'remove':
      return `Remove ${entityLabel} (ID: ${modification.entityId})`;

    case 'modify':
      if (modification.changes.description) {
        return modification.changes.description;
      }
      return `Modify ${entityLabel} (ID: ${modification.entityId})`;

    default:
      return 'Unknown modification';
  }
}

/**
 * Calculate estimated run time based on modifications
 */
export function estimateRunTime(modifications: SimulationModification[]): {
  seconds: number;
  label: string;
} {
  // Base time
  let seconds = 5;

  // Add time based on modification complexity
  for (const mod of modifications) {
    switch (mod.entity) {
      case 'teacher':
        seconds += mod.type === 'add' ? 3 : 2;
        break;
      case 'room':
        seconds += 2;
        break;
      case 'student':
        seconds += (mod.changes.count || 0) > 20 ? 5 : 3;
        break;
      case 'rule':
        seconds += 4; // Rules can have broader impact
        break;
      default:
        seconds += 2;
    }
  }

  // Format label
  const label = seconds < 60 ? `~${seconds} seconds` : `~${Math.round(seconds / 60)} minute(s)`;

  return { seconds, label };
}

/**
 * Check if a scenario can be promoted to live
 */
export function canPromoteToLive(scenario: SimulationScenario): {
  canPromote: boolean;
  reasons: string[];
} {
  const reasons: string[] = [];

  if (scenario.status !== 'completed') {
    reasons.push('Scenario must be completed');
  }

  if (!scenario.results) {
    reasons.push('Scenario must have results');
  } else {
    if (!scenario.results.feasible) {
      reasons.push('Scenario must be feasible');
    }

    if (scenario.results.stats.ruleViolations.hard > 0) {
      reasons.push('Scenario has hard rule violations');
    }

    if (scenario.results.stats.conflictCount > 5) {
      reasons.push('Scenario has too many conflicts');
    }
  }

  return {
    canPromote: reasons.length === 0,
    reasons,
  };
}

